<template>
  <div class="container-fluid admin-page">
    <div class="title-area">
      <h2>ユーザー管理</h2>
      <div class="create-btn">
        <button class="btn btn-info" @click="create">
          <i class="fas fa-plus"></i><span class="ml-1">新規作成</span>
        </button>
      </div>
      <div class="csv-btn-wrapper">
        <label class="btn btn-secondary mb0 ml8" for="csvFileInput">
          <i class="fa fa-upload" v-if="!csvState.isUploading"></i>
          <i class="fa fa-spinner fa-spin" v-else></i>
          一括
        </label>
        <input
          type="file"
          id="csvFileInput"
          style="display: none;"
          accept=".csv"
          @click="$event.target.value = null"
          @change="openConfirmCsvUploadModal"
          :disabled="csvState.isUploading"
        >
        <button class="btn btn-secondary ml8" @click="downloadCSV" :disabled="csvState.isDownloading">
          <i class="fa fa-download" v-if="!csvState.isDownloading"></i>
          <i class="fa fa-spinner fa-spin" v-else></i>
          一括
        </button>
      </div>
    </div>
    <div class="search-area">
      <form class="search-form" v-on:submit.prevent>
        <div class="form-group">
          <label class="form-control-label">事業所</label>
          <select class="form-control" v-model="searchState.search.group2Id">
            <option :value="null"></option>
            <option v-for="e in searchState.groups2" :key="e.id" :value="e.id">
              {{e.name}}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-control-label">部署</label>
          <select class="form-control" v-model="searchState.search.group3Id">
            <option :value="null"></option>
            <option v-for="e in selectableSearchGroup3" :key="e.id" :value="e.id">
              {{e.name}}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-control-label">ユーザーID・ユーザー名</label>
          <input type="text" class="form-control" v-model="searchState.search.userIdOrUserName">
        </div>
        <div class="form-group">
          <label class="form-control-label">権限</label>
          <select class="form-control" v-model="searchState.search.role">
            <option :value="null"></option>
            <option v-for="e in selectableRoles" :key="e.key" :value="e.key">
              {{e.val}}
            </option>
          </select>
        </div>
        <div class="form-group search-btn ta-center">
          <button type="submit" class="btn btn btn-primary" @click="doSearch">
            検索
          </button>
        </div>
      </form>
    </div>
    <AdminListTable
      :display-data="displayData"
      :fields="fields"
      :custom-buttons="customButtons"
      :can-delete="canDeleteUser"
      :can-edit="canEditUser"
      :always-show-buttons="true"
      @start-edit="edit"
      @start-destroy="tryDestroy"
    />
    <UserEditModal
      v-if="editState.showEditModal"
      :edit-mode="editState.editMode"
      :editing-user="editState.editingUser"
      :groups2="searchState.groups2"
      :groups3="searchState.groups3"
      :selectable-roles="selectableRoles"
      :http-error-msgs="editState.httpErrorMsgs"
      @create="onCreateUser"
      @update-profile="onUpdateProfile"
      @update-password="onUpdatePassword"
      @close="editState.showEditModal = false"
    />
    <Modal
      v-if="editState.showDestroyModal"
      :title="destroyModalTitle"
      :show-bottom-dismiss-button="true"
      @close="doDestroy"
      @dismiss="editState.showDestroyModal = false"
    >
      <template v-slot:body>
        <div>
          <p class="p1">ユーザーID<span class="colon"></span>{{editState.destroyingUser?.username}}</p>
          <p class="p2 font-mtx-desc" v-html="destroyModalMsg" />
        </div>
      </template>
    </Modal>
    <Modal
      v-if="editState.showErrorModal"
      :title="errorModalTitle"
      :show-bottom-dismiss-button="false"
      @close="editState.showErrorModal = false"
    >
      <template v-slot:body>
        <div>
          <p class="p2 font-mtx-desc" v-html="errorModalMsg"></p>
        </div>
      </template>
    </Modal>
    <UserCsvUploadModal
      :show-confirm="csvState.showConfirmCsvUploadModal"
      :show-complete="csvState.showCsvUploadCompleteModal"
      :show-error="csvState.showCsvErrorModal"
      :uploading-csv="csvState.uploadingCsv"
      :csv-result="csvState.csvResult"
      :csv-error-body="csvState.csvErrorBody"
      :is-uploading="csvState.isUploading"
      @confirm="uploadCSV"
      @dismiss-confirm="closeConfirmCsvUploadModal"
      @dismiss-complete="closeCsvUploadCompleteModal"
      @dismiss-error="csvState.showCsvErrorModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import userApi from 'src/apis/user';
import group2Api from 'src/apis/group2';
import group3Api from 'src/apis/group3';
import { computed, onMounted, reactive, type ComputedRef } from 'vue';
import type { Lov } from 'src/models/master';
import type { AdminGroup2 } from 'src/models/group2';
import type { AdminGroup3 } from 'src/models/group3';
import type { AdminUser, AdminUserCsvResult, AdminUserSearchParams } from 'src/models/user';
import type {
  AdminUserCreateParams,
  AdminUserPasswordUpdateParams,
  AdminUserUpdateParams,
} from 'src/models/apis/user/adminUserRequest';
import { useAuth } from 'src/composables/useAuth';
import { useReactiveMasters } from 'src/composables/useMasters';
import { getErrorMessages } from 'src/lib/errMsgHelper';
import { downloadBlob } from 'src/lib/downloadHelper';
import { dtFormat } from 'src/lib/dateHelper';
import { lineBreakToBR } from 'src/lib/utils';
import type { AxiosError } from 'axios';
import type { AdminListTableField, CustomButton } from 'src/models/adminListTable';
import UserEditModal from 'src/components/Admin/User/UserEditModal.vue';
import UserCsvUploadModal from 'src/components/Admin/User/UserCsvUploadModal.vue';

type UsersDisplayRow = AdminUser & { gName: string; roleDisp: string | number };

interface UsersSearchState {
  roles: Lov[];
  groups2: AdminGroup2[];
  groups3: AdminGroup3[];
  search: AdminUserSearchParams;
}

interface UsersEditState {
  showEditModal: boolean;
  editMode: 'create' | 'edit' | 'editPassword';
  editingUser: AdminUser | null;
  httpErrorMsgs: string[];
  showDestroyModal: boolean;
  destroyingUser: AdminUser | null;
  showErrorModal: boolean;
}

interface UsersCsvState {
  uploadingCsv: File | null;
  csvResult: AdminUserCsvResult | null;
  csvErrorBody: string;
  isDownloading: boolean;
  isUploading: boolean;
  showConfirmCsvUploadModal: boolean;
  showCsvUploadCompleteModal: boolean;
  showCsvErrorModal: boolean;
}

const initSearchState = (): AdminUserSearchParams => {
  return {
    group2Id: null,
    group3Id: null,
    userIdOrUserName: null,
    role: null,
  };
};

const searchState = reactive<UsersSearchState>({
  roles: [],
  groups2: [],
  groups3: [],
  search: initSearchState(),
});
const filteredUsers = reactive<{ list: AdminUser[] }>({ list: [] });
const editState = reactive<UsersEditState>({
  showEditModal: false,
  editMode: 'edit',
  editingUser: null,
  httpErrorMsgs: [],
  showDestroyModal: false,
  destroyingUser: null,
  showErrorModal: false,
});
const csvState = reactive<UsersCsvState>({
  uploadingCsv: null,
  csvResult: null,
  csvErrorBody: '',
  isDownloading: false,
  isUploading: false,
  showConfirmCsvUploadModal: false,
  showCsvUploadCompleteModal: false,
  showCsvErrorModal: false,
});

const auth = useAuth();
const { masters, resolveMastersPromise } = useReactiveMasters();

const selectableSearchGroup3 = computed<AdminGroup3[]>(() => {
  if (searchState.search.group2Id == null) return searchState.groups3;

  return searchState.groups3.filter((e) => {
    return e.parentId === searchState.search.group2Id;
  });
});
const selectableRoles = computed<Lov[]>(() => {
  const loginUser = auth.user;
  if (!loginUser) return [];

  // 自分と同じか自分より弱い権限しか選択できない.
  return searchState.roles.filter((e) => parseInt(e.key) >= loginUser.role);
});
const roleLabelMap = computed<Map<number, string>>(() => {
  return new Map(searchState.roles.map((e) => [parseInt(e.key), e.val]));
});
const isLoginUser = (otherUser: { id: number } | null): boolean => {
  if (!auth.user || !otherUser) return false;

  return auth.user.id === otherUser.id;
};
const canEditUser = (otherUser: AdminUser): boolean => {
  if (!otherUser || !otherUser.groupInfo) return false;

  const loginUser = auth.user;
  if (!loginUser) return false;

  const otherUserRole = Number(otherUser.role);
  if (loginUser.role > otherUserRole) return false;

  if (loginUser.hasRoleOperationsAdmin) {
    return loginUser.groupInfo.g1id === otherUser.groupInfo.g1id;
  } else if (loginUser.hasRoleBranchAdmin) {
    return loginUser.groupInfo.g2id === otherUser.groupInfo.g2id;
  } else if (loginUser.hasRoleDepartmentAdmin) {
    return loginUser.groupInfo.g3id === otherUser.groupInfo.g3id;
  }
  return false;
};
const canDeleteUser = (otherUser: AdminUser): boolean => {
  return canEditUser(otherUser) && !isLoginUser(otherUser);
};
const displayData: ComputedRef<UsersDisplayRow[]> = computed(() => {
  return filteredUsers.list.map(user => {
    const gName = `${user.groupInfo.g2name}\n${user.groupInfo.g3name}`;
    return {
      ...user,
      gName: lineBreakToBR(gName),
      roleDisp: roleLabelMap.value.get(user.role) ?? user.role,
    };
  });
});

const filterSearch = (usersOrig: AdminUser[]): void => {
  filteredUsers.list = usersOrig.filter(user => {
    if (searchState.search.group2Id != null &&
      user.groupInfo.g2id !== searchState.search.group2Id) {
      return false;
    }
    if (searchState.search.group3Id != null &&
      user.groupInfo.g3id !== searchState.search.group3Id) {
      return false;
    }
    if (searchState.search.userIdOrUserName != null &&
      searchState.search.userIdOrUserName !== '' &&
      user.username.indexOf(searchState.search.userIdOrUserName) < 0 &&
      user.displayName.indexOf(searchState.search.userIdOrUserName) < 0) {
      return false;
    }
    if (searchState.search.role != null &&
      searchState.search.role !== '' &&
      user.role !== parseInt(searchState.search.role)) {
      return false;
    }
    return true;
  });
};
const doSearch = async(): Promise<void> => {
  filteredUsers.list = [];
  const users = await userApi.adminIndex({});
  if (!users || users.length === 0) return;

  filterSearch(users);
};

const fields: AdminListTableField<UsersDisplayRow>[] = [
  { name: 'gName', label: '所属組織', type: 'html', class: 'ws-nowrap' },
  { name: 'username', label: 'ユーザーID' },
  { name: 'displayName', label: 'ユーザー名' },
  { name: 'roleDisp', label: '権限', class: 'ws-nowrap' },
];

const create = (): void => {
  editState.editMode = 'create';
  editState.editingUser = null;
  editState.httpErrorMsgs = [];
  editState.showEditModal = true;
};
const edit = (obj: AdminUser): void => {
  editState.editMode = 'edit';
  editState.editingUser = obj;
  editState.httpErrorMsgs = [];
  editState.showEditModal = true;
};
const editPassword = (obj: AdminUser): void => {
  editState.editMode = 'editPassword';
  editState.editingUser = obj;
  editState.httpErrorMsgs = [];
  editState.showEditModal = true;
};
const tryDestroy = (obj: AdminUser): void => {
  if (!canDeleteUser(obj)) return;

  editState.destroyingUser = obj;
  editState.showDestroyModal = true;
};
const doDestroy = async(): Promise<void> => {
  if (!editState.destroyingUser) return;

  try {
    await userApi.adminDestroy(editState.destroyingUser.id);
    editState.showDestroyModal = false;
    void doSearch();
  } catch {
    editState.showDestroyModal = false;
    editState.showErrorModal = true;
  }
};

const runSave = async(apiCall: () => Promise<unknown>): Promise<void> => {
  try {
    await apiCall();
    editState.httpErrorMsgs = [];
    editState.showEditModal = false;
    void doSearch();
  } catch (err) {
    editState.httpErrorMsgs = getErrorMessages(err as AxiosError);
  }
};
const onCreateUser = (payload: AdminUserCreateParams): Promise<void> => {
  return runSave(() => userApi.adminCreate(payload));
};
const onUpdateProfile = (id: number, payload: AdminUserUpdateParams): Promise<void> => {
  return runSave(() => userApi.adminUpdate(id, payload));
};
const onUpdatePassword = (id: number, payload: AdminUserPasswordUpdateParams): Promise<void> => {
  return runSave(() => userApi.adminPasswordUpdate(id, payload));
};

const customButtons: CustomButton<AdminUser>[] = [
  {
    label: 'パスワード変更',
    clickHandler: (obj: AdminUser) => {
      editPassword(obj);
    },
    isVisible: () => true,
    isDisabled: (obj: AdminUser) => {
      return !canEditUser(obj);
    },
  },
];

const openConfirmCsvUploadModal = (evt: Event): void => {
  const eventTarget = evt.target as HTMLInputElement;
  if (!eventTarget || !eventTarget.files) return;

  const file = eventTarget.files[0];
  if (!file) return;

  if (!file.name.toLowerCase().endsWith('.csv')) {
    eventTarget.value = '';
    csvState.csvErrorBody = 'CSVファイル（拡張子.csv）を選択してください';
    csvState.showCsvErrorModal = true;
    return;
  }

  csvState.uploadingCsv = file;
  csvState.showConfirmCsvUploadModal = true;
};
const downloadCSV = async(): Promise<void> => {
  csvState.isDownloading = true;
  try {
    // NOTE: 送信キーは従来挙動を維持する（user_id_or_user_name のまま）.
    const searchParams = {
      group2_id: searchState.search.group2Id,
      group3_id: searchState.search.group3Id,
      user_id_or_user_name: searchState.search.userIdOrUserName,
      role: searchState.search.role,
    };
    const { data: csvBlob } = await userApi.adminDownloadCSV(searchParams);
    const timestamp = dtFormat(new Date(), 'yyyymmdd_HHMMSS');
    const filename = `ユーザー一覧_${timestamp}`;
    downloadBlob(csvBlob, filename);
    csvState.isDownloading = false;
  } catch {
    csvState.csvErrorBody = 'CSVダウンロードに失敗しました。再度操作を行ってください';
    csvState.showCsvErrorModal = true;
    csvState.isDownloading = false;
  }
};
const closeConfirmCsvUploadModal = (): void => {
  csvState.uploadingCsv = null;
  csvState.showConfirmCsvUploadModal = false;
};
const closeCsvUploadCompleteModal = (): void => {
  csvState.csvResult = null;
  csvState.showCsvUploadCompleteModal = false;
};
const uploadCSV = async(): Promise<void> => {
  if (!csvState.uploadingCsv) return;

  csvState.isUploading = true;
  try {
    const formData = new FormData();
    formData.append('file', csvState.uploadingCsv, csvState.uploadingCsv.name);
    const csvResult = await userApi.adminUploadCSV(formData);
    csvState.csvResult = {
      ...csvResult,
      hasErrors: csvResult.failures.length > 0,
    };
    csvState.showCsvUploadCompleteModal = true;
    closeConfirmCsvUploadModal();
    csvState.isUploading = false;
    void doSearch();
  } catch {
    csvState.csvErrorBody = 'CSVアップロードに失敗しました。再度操作を行ってください';
    csvState.showCsvErrorModal = true;
    closeConfirmCsvUploadModal();
    csvState.isUploading = false;
  }
};

onMounted(async() => {
  const [groups2, groups3] = await Promise.all([
    group2Api.adminIndex(),
    group3Api.adminIndex(),
  ]);
  searchState.groups2 = groups2;
  searchState.groups3 = groups3;
  void doSearch();
  await resolveMastersPromise();
  searchState.roles = masters.lov.roles;
});

const destroyModalTitle = '削除';
const destroyModalMsg = 'を削除してもよろしいですか？';
const errorModalTitle = 'エラー';
const errorModalMsg = '不明なエラーです。ネットワークの状態をご確認いただき再度お試し下さい。';
</script>

<style lang="scss" scoped>
.csv-btn-wrapper {
  margin-left: auto;
}
</style>
